import { Toast } from './AppToast';
import { apiCall, apiGet } from '../utils/Api';
import { removePushSubscription, syncPushSubscription } from '../utils/PushNotifications';

import localforage from 'localforage';
import moment from 'moment';
import 'moment/locale/es';
moment.locale('es');

/**
 * Clase para manejo de autenticación
 * Implementa patrón Singleton
 * @class AppAuth
 */
class AppAuth {
	/**
	 * Constructor del AppAuth (Singleton)
	 */
	constructor() {
		if (AppAuth._instance) {
			return AppAuth._instance;
		}

		/** @type {boolean} */
		this.initialized = false;
		/** @type {string|null} */
		this.token = null;
		/** @type {AppManager|null} */
		this.app = null;

		AppAuth._instance = this;
	}

	/**
	 * Inicializa el módulo de autenticación
	 * @async
	 * @param {AppManager} app - Instancia de AppManager
	 * @returns {Promise<void>}
	 * @throws {Error} Si hay error en la inicialización
	 */
	async init(app) {
		if (this.initialized) {
			return;
		}

		try {
			this.app = app;
			const access = await localforage.getItem('access');
			if (access && access.token && moment().isBefore(access.expiration)) {
				this.token = access.token;
			} else {
				this.token = null;
			}
			this.initialized = true;
		} catch (error) {
			this.token = null;
			this.initialized = true;
			throw error;
		}
	}

	/**
	 * Verifica si el usuario está autenticado
	 * @async
	 * @returns {Promise<boolean>} true si está autenticado
	 */
	async isAuthenticated() {
		try {
			const access = await localforage.getItem('access');
			if (access && access.token) {
				if (moment().isBefore(access.expiration)) {
					this.token = access.token;
					return true;
				} else {
					return false;
				}
			} else {
				return false;
			}
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Inicia sesión con username y password
	 * @async
	 * @param {string} username - Nombre de usuario
	 * @param {string} password - Contraseña
	 * @param {Function} success - Callback de éxito
	 * @param {Function} failure - Callback de fallo
	 * @returns {Promise<void>}
	 */
	async login(username, password, success, failure) {
		if (!username || !password) {
			return;
		}

		Toast.show('Iniciando Sesión', 'warning', 2000);
		try {
			const url = `/token?username=${encodeURIComponent(username)}`;
			const data = await apiCall(url, {
				method: 'GET',
				requireAuth: false,
				headers: {
					'Authorization': 'Basic ' + btoa(`${username}:${password}`)
				}
			});
			if (data.class === 'success') {
				await localforage.setItem('access', data.info);
				this.token = data.info.token;

				await this.app.db.updateMember(data.member);
				await this.app.db.updateApplications(data.applications);
				syncPushSubscription().catch(() => {});
				
				if (typeof success === 'function') success(data);

			} else {
				Toast.show(data.html, data.class);
				if (typeof failure === 'function') failure(data);
			}
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Cierra sesión del usuario
	 * @async
	 * @returns {Promise<void>}
	 */
	async logout() {
		try {
			await removePushSubscription();
		} catch (error) {
			console.warn('push unsubscribe', error);
		}
		try {
			if (!this.token || !this.app.db.member?.member_id) {
				return;
			}

			const url = `/logout?token=${this.token}&member_id=${this.app.db.member.member_id}`;
			await apiGet(url, { requireAuth: false });
		} catch (error) {
			throw error;
		} finally {
			await localforage.clear();
			if (this.app && (this.app.getCurrentControllerName() === null || this.app.getCurrentControllerName() !== 'login')) {
				this.app.load('login');
			}
		}
	}

	async clear() {
		await localforage.clear();

		if ('serviceWorker' in navigator) {
			try {
				await caches.delete(await this.app.getVersion());
				const registrations = await navigator.serviceWorker.getRegistrations();
				for (const registration of registrations) {
					await registration.unregister();
				}
				window.location.replace(window.location.href);
		} catch (error) {
			throw error;
		}
		}
	}

	/**
	 * Solicita recuperación de contraseña
	 * @async
	 * @param {string} username - Nombre de usuario
	 * @param {Function} success - Callback de éxito
	 * @param {Function} failure - Callback de fallo
	 * @returns {Promise<void>}
	 */
	async recoverPassword(username, success, failure) {
		if (!username) {
			return;
		}

		try {
			const url = `/recover?username=${encodeURIComponent(username)}`;
			const data = await apiGet(url, { requireAuth: false });
			Toast.show(data.html, data.class);

			if (data.class === 'success') {
				if (typeof success === 'function') success(data);
			} else {
				if (typeof failure === 'function') failure(data);
			}
		} catch (error) {
			if (typeof failure === 'function') failure(error);
			throw error;
		}
	}
}

export { AppAuth };
